import { useState, useCallback, useRef, useMemo } from 'react';

/**
 * Drag and drop state data
 */
export interface DragDropData {
  isDragging: boolean;
  isDragOver: boolean;
}

/**
 * Hook for handling drag and drop functionality
 */
export function useDragDrop(onFilesDropped: (files: File[]) => void, disabled = false, acceptedMimeTypes?: string[]) {
  const [dragData, setDragData] = useState<DragDropData>({
    isDragging: false,
    isDragOver: false,
  });

  const dragCounter = useRef(0);

  /**
   * Handles drag enter events
   */
  const handleDragEnter = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      dragCounter.current++;

      if (e.dataTransfer?.items != null && e.dataTransfer.items.length > 0) {
        setDragData({
          isDragging: true,
          isDragOver: true,
        });
      }
    },
    [disabled],
  );

  /**
   * Handles drag leave events
   */
  const handleDragLeave = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      dragCounter.current--;

      if (dragCounter.current === 0) {
        setDragData({
          isDragging: false,
          isDragOver: false,
        });
      }
    },
    [disabled],
  );

  /**
   * Handles drag over events
   */
  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      // Set the drag effect to copy to show the user they can drop here
      if (e.dataTransfer != null) {
        e.dataTransfer.dropEffect = 'copy';
      }
    },
    [disabled],
  );

  /**
   * Handles drop events
   */
  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      // Reset drag state
      dragCounter.current = 0;
      setDragData({
        isDragging: false,
        isDragOver: false,
      });

      if (e.dataTransfer?.files != null && e.dataTransfer.files.length > 0) {
        const files = Array.from(e.dataTransfer.files);
        onFilesDropped(files);
      }
    },
    [disabled, onFilesDropped],
  );

  /**
   * Handles file input change events
   */
  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (disabled) return;

      if (e.target.files != null && e.target.files.length > 0) {
        const files = Array.from(e.target.files);
        onFilesDropped(files);

        // Clear the input so the same file can be selected again
        e.target.value = '';
      }
    },
    [disabled, onFilesDropped],
  );

  /**
   * Creates drag and drop event handlers for a container element
   */
  const dragDropProps = useMemo(
    () => ({
      onDragEnter: handleDragEnter,
      onDragLeave: handleDragLeave,
      onDragOver: handleDragOver,
      onDrop: handleDrop,
    }),
    [handleDragEnter, handleDragLeave, handleDragOver, handleDrop],
  );

  /**
   * Creates props for a file input element
   */
  const fileInputProps = useMemo(
    () => ({
      type: 'file' as const,
      multiple: true,
      accept: acceptedMimeTypes?.join(',') ?? '*/*',
      onChange: handleFileInputChange,
      style: { display: 'none' } as const,
    }),
    [handleFileInputChange, acceptedMimeTypes],
  );

  return {
    dragData,
    dragDropProps,
    fileInputProps,
  };
}
